import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, EyeOff, Wrench } from "lucide-react";
import type { NoindexImportantDetails, PageIssue } from "@/types/technicalAudit";

// noindex_important: the detector already proved this noindexed page is
// important (in the XML sitemap + internal inlinks + shallow crawl depth).
// This card surfaces that importance evidence and states EXACTLY what Apply
// writes: the SEO-plugin robots meta flips from "noindex" to "index"
// (Yoast `_yoast_wpseo_meta-robots-noindex`, RankMath `rank_math_robots`).
// Only page-level META noindex has this write path — header / robots.txt
// blocks need a manual fix (the help copy says so).

interface NoindexImportantCardProps {
	issue: PageIssue;
}

const NOINDEX_SOURCE_LABELS: Record<string, string> = {
	meta: "Robots meta tag",
	header: "X-Robots-Tag header",
	robots_txt: "robots.txt",
};

export function NoindexImportantCard({ issue }: NoindexImportantCardProps) {
	const details = (issue.issue_details || {}) as NoindexImportantDetails;
	const noindexSource = details.noindex_source || "meta";
	const noindexSourceLabel =
		NOINDEX_SOURCE_LABELS[noindexSource] || noindexSource;
	const status = details.indexability_status || "noindex";
	const evidenceBadges: string[] = [];
	if (typeof details.unique_inlinks === "number") {
		evidenceBadges.push(
			`${details.unique_inlinks} internal inlink${
				details.unique_inlinks === 1 ? "" : "s"
			}`,
		);
	}
	if (typeof details.crawl_depth === "number") {
		evidenceBadges.push(`Crawl depth ${details.crawl_depth}`);
	}
	if (details.in_sitemap) {
		evidenceBadges.push("In XML sitemap");
	}

	return (
		<div className="space-y-4">
			{/* Critical banner — a proven-important page is hidden from Google. */}
			<div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
				<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
					<AlertTriangle className="h-5 w-5 text-destructive" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-destructive">
						Noindex on Important Page
					</h4>
					<p className="mt-1 text-sm text-foreground/80">
						This page is tagged &quot;{status}&quot;, so Google will not index
						it — yet the crawl shows it is an important page on this site.
					</p>
				</div>
			</div>

			{/* Importance evidence */}
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-coral-light">
							<EyeOff className="h-4 w-4 text-coral-dark" />
						</div>
						Why this page matters
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3 pt-0">
					<div className="flex flex-wrap items-center gap-2">
						<Badge
							variant="outline"
							className="border-destructive/30 bg-destructive/5 text-destructive"
						>
							Noindex source: {noindexSourceLabel}
						</Badge>
						{evidenceBadges.map((label) => (
							<Badge
								key={label}
								variant="outline"
								className="border-coral/40 bg-coral-light text-coral-dark"
							>
								{label}
							</Badge>
						))}
					</div>
					{details.importance_reason && (
						<div className="rounded-lg border border-border bg-warm-bg p-3">
							<p className="text-sm text-foreground/80">
								{details.importance_reason}
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* What Apply changes */}
			<Card className="border-teal/30 bg-teal-light/40 shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-sm font-semibold text-teal-dark">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-light">
							<Wrench className="h-4 w-4 text-teal-dark" />
						</div>
						What Apply changes
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2 pt-0">
					<div className="rounded-lg border border-teal/30 bg-card p-3">
						<code className="text-sm font-medium text-foreground">
							meta robots: noindex → index
						</code>
					</div>
					<p className="text-xs text-foreground/70">
						Apply updates the page&apos;s SEO-plugin robots meta (Yoast{" "}
						<code>_yoast_wpseo_meta-robots-noindex</code>, RankMath{" "}
						<code>rank_math_robots</code>) so Google can index the page again.
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
